import React, { useContext, useState } from "react";
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "../../contexts/InfoContext";
import { useAuth } from "../../contexts/AuthContext";
import { SpinnerContext } from "../../contexts/SpinnerContext";
import useUserClaims from "../../hooks/useUserClaims";
import getRequiredUserData from "../../utils/getBranchInfo";
import { createBillType, updateBillType } from "../../api/bills/billTypes";
import capitalizeString from "../../utils/capitalizeString";

// Validation schema
const BillTypeFormValidationSchema = Yup.object().shape({
  type: Yup.string().required("Bill type is required"),
});

const BillTypeForm = ({
  isEditDialogOpen = false,
  closeEditDialog,
  editData = null,
}) => {
  const [showForm, setShowForm] = useState(false);
  const { openSnackbar } = useSnackbar();
  const { user } = useAuth();
  const theme = useTheme();
  const { setIsSubmitting } = useContext(SpinnerContext);
  const userClaims = useUserClaims(user);
  const branchData = getRequiredUserData();

  const handleButtonClick = () => {
    if (!branchData.requiredId) {
      openSnackbar(
        "Branch information is not found. Please check your connection.",
        "info"
      );
    } else {
      setShowForm(true);
    }
  };

  const formik = useFormik({
    initialValues: {
      type: editData ? editData.type : "",
    },
    validationSchema: BillTypeFormValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const type = capitalizeString(values.type);
        const billTypeData = {
          type: type,
          branchId: branchData.requiredId,
        };

        if (editData) {
          // Update existing bill type
          const res = await updateBillType(user, editData.id, billTypeData);
          openSnackbar(
            res.data.message || "Bill type updated successfully!",
            "success"
          );
        } else {
          // Create new bill type
          const res = await createBillType(user, billTypeData);
          openSnackbar(
            res.data.message || "Bill type created successfully!",
            "success"
          );
        }

        handleCloseForm();
      } catch (error) {
        openSnackbar(
          error.message ||
            "An unexpected error occurred. Please check your connection.",
          "error"
        );
      }
      setIsSubmitting(false);
    },
  });

  const handleCloseForm = () => {
    setShowForm(false);
    if (closeEditDialog) {
      closeEditDialog();
    }
    formik.resetForm();
  };

  return (
    <div>
      {userClaims.admin || userClaims.subAdmin ? (
        <div>
          {!editData && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleButtonClick}
            >
              Create New Bill Type
            </Button>
          )}

          <Dialog
            open={Boolean(showForm || isEditDialogOpen)}
            onClose={handleCloseForm}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ backgroundColor: theme.palette.background.alt }}>
              {editData ? "Edit Bill Type" : "Create New Bill Type"}
            </DialogTitle>
            <DialogContent
              sx={{ backgroundColor: theme.palette.background.alt }}
            >
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      name="type"
                      label="Bill Type"
                      required
                      value={formik.values.type}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                        style: {
                          marginTop: "8px",
                          color: theme.palette.secondary[100],
                        },
                      }}
                      error={formik.touched.type && Boolean(formik.errors.type)}
                      helperText={formik.touched.type && formik.errors.type}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    container
                    alignItems="end"
                    justifyContent="end"
                  >
                    <DialogActions
                      sx={{ backgroundColor: theme.palette.background.alt }}
                    >
                      <Button
                        variant="contained"
                        onClick={handleCloseForm}
                        color="secondary"
                      >
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained" color="primary">
                        {editData ? "Update" : "Create"}
                      </Button>
                    </DialogActions>
                  </Grid>
                </Grid>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      ) : null}
    </div>
  );
};

export default BillTypeForm;
